import type { CSSProperties } from 'react';

export interface WallpaperDef {
  id: string;
  name: string;
  css: CSSProperties;
  swatch: string;
}

export const WALLPAPERS: WallpaperDef[] = [
  { id: 'default', name: 'Default', css: {}, swatch: 'linear-gradient(135deg,#1a1b26,#141863)' },
  { id: 'bloom', name: 'Bloom', css: { background: 'linear-gradient(135deg,#1e3a8a 0%,#7c3aed 50%,#db2777 100%)' }, swatch: 'linear-gradient(135deg,#1e3a8a,#7c3aed,#db2777)' },
  { id: 'sunset', name: 'Sunset', css: { background: 'linear-gradient(135deg,#7c2d12 0%,#db2777 55%,#f59e0b 100%)' }, swatch: 'linear-gradient(135deg,#7c2d12,#db2777,#f59e0b)' },
  { id: 'forest', name: 'Forest', css: { background: 'linear-gradient(135deg,#052e16 0%,#15803d 55%,#a3e635 100%)' }, swatch: 'linear-gradient(135deg,#052e16,#15803d,#a3e635)' },
  { id: 'ocean', name: 'Ocean', css: { background: 'linear-gradient(135deg,#082f49 0%,#0284c7 55%,#22d3ee 100%)' }, swatch: 'linear-gradient(135deg,#082f49,#0284c7,#22d3ee)' },
  { id: 'midnight', name: 'Midnight', css: { background: 'linear-gradient(135deg,#020617 0%,#1e1b4b 60%,#312e81 100%)' }, swatch: 'linear-gradient(135deg,#020617,#1e1b4b,#312e81)' },
  { id: 'candy', name: 'Candy', css: { background: 'linear-gradient(135deg,#831843 0%,#a855f7 55%,#6366f1 100%)' }, swatch: 'linear-gradient(135deg,#831843,#a855f7,#6366f1)' },
  // Windows 11-style wallpapers (radial bloom ribbons + abstract flows)
  {
    id: 'win11-bloom',
    name: 'Win Bloom',
    css: {
      background: `
        radial-gradient(ellipse 62% 48% at 50% 58%, rgba(0,120,215,0.55) 0%, rgba(0,120,215,0.12) 45%, transparent 70%),
        radial-gradient(ellipse 50% 40% at 44% 46%, rgba(40,180,240,0.5) 0%, transparent 60%),
        radial-gradient(ellipse 55% 45% at 58% 60%, rgba(0,60,140,0.65) 0%, transparent 65%),
        linear-gradient(160deg, #0a1628 0%, #0e2a52 45%, #0a1a33 100%)`,
    },
    swatch: 'radial-gradient(circle at 50% 55%, #2e8ae6, #0a1628)',
  },
  {
    id: 'win11-light',
    name: 'Win Light',
    css: {
      background: `
        radial-gradient(ellipse 60% 45% at 52% 55%, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.1) 50%, transparent 72%),
        radial-gradient(ellipse 45% 38% at 40% 40%, rgba(120,190,255,0.6) 0%, transparent 60%),
        radial-gradient(ellipse 55% 48% at 62% 62%, rgba(70,130,220,0.45) 0%, transparent 65%),
        linear-gradient(150deg, #dfe9f5 0%, #bcd4f0 50%, #9fc0ec 100%)`,
    },
    swatch: 'radial-gradient(circle at 50% 55%, #ffffff, #9fc0ec)',
  },
  {
    id: 'win11-glow',
    name: 'Win Glow',
    css: {
      background: `
        radial-gradient(ellipse 55% 42% at 50% 62%, rgba(0,255,200,0.35) 0%, rgba(0,140,255,0.2) 40%, transparent 70%),
        radial-gradient(ellipse 70% 55% at 50% 55%, rgba(0,80,160,0.4) 0%, transparent 75%),
        linear-gradient(180deg, #050a14 0%, #071527 55%, #03070f 100%)`,
    },
    swatch: 'radial-gradient(circle at 50% 60%, #00c8a0, #050a14)',
  },
  {
    id: 'win11-sunset',
    name: 'Win Sunset',
    css: {
      background: `
        radial-gradient(ellipse 60% 45% at 50% 60%, rgba(255,140,60,0.5) 0%, rgba(220,60,80,0.25) 45%, transparent 70%),
        radial-gradient(ellipse 50% 40% at 42% 45%, rgba(255,90,120,0.4) 0%, transparent 60%),
        radial-gradient(ellipse 65% 50% at 58% 62%, rgba(90,20,80,0.55) 0%, transparent 68%),
        linear-gradient(160deg, #1a0b2e 0%, #4a1030 50%, #12061f 100%)`,
    },
    swatch: 'radial-gradient(circle at 50% 58%, #ff8c3c, #1a0b2e)',
  },
  {
    id: 'win11-flow',
    name: 'Win Flow',
    css: {
      background: `
        conic-gradient(from 210deg at 55% 45%, rgba(0,200,255,0.25) 0deg, rgba(120,60,255,0.3) 90deg, rgba(0,120,215,0.2) 180deg, transparent 260deg),
        radial-gradient(ellipse 65% 50% at 50% 50%, rgba(30,60,140,0.5) 0%, transparent 70%),
        linear-gradient(135deg, #0b0f2a 0%, #141a4d 50%, #090c22 100%)`,
    },
    swatch: 'conic-gradient(from 210deg, #00c8ff, #783cff, #0b0f2a)',
  },
  {
    id: 'win11-aurora',
    name: 'Win Aurora',
    css: {
      background: `
        radial-gradient(ellipse 80% 30% at 50% 20%, rgba(60,255,180,0.18) 0%, transparent 60%),
        radial-gradient(ellipse 70% 35% at 30% 35%, rgba(0,180,255,0.22) 0%, transparent 60%),
        radial-gradient(ellipse 75% 40% at 70% 45%, rgba(150,80,255,0.2) 0%, transparent 60%),
        linear-gradient(180deg, #04060e 0%, #060b18 60%, #02040a 100%)`,
    },
    swatch: 'linear-gradient(135deg,#04060e,#0aa0ff,#0affb4)',
  },
];

// Resolves the stored wallpaper value to a style object.
// Values: preset id | 'default' | 'color:#rrggbb' | 'url:<image url>'
export function wallpaperStyle(wallpaper: string): CSSProperties | undefined {
  if (!wallpaper || wallpaper === 'default') return undefined;
  if (wallpaper.startsWith('color:')) {
    return { background: wallpaper.slice('color:'.length) };
  }
  if (wallpaper.startsWith('url:')) {
    return {
      backgroundImage: `url(${wallpaper.slice('url:'.length)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return WALLPAPERS.find((w) => w.id === wallpaper)?.css;
}

export function wallpaperLabel(wallpaper: string): string {
  if (!wallpaper || wallpaper === 'default') return 'Default';
  if (wallpaper.startsWith('color:')) return 'Custom color';
  if (wallpaper.startsWith('url:')) return 'Custom image';
  return WALLPAPERS.find((w) => w.id === wallpaper)?.name || 'Default';
}
